import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemoTour } from '../context/DemoTourContext';
import { Button } from './ui/button';

const Navbar = () => {
  const { user } = useAuth();
  const { tourActive, startTour, endTour } = useDemoTour();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleTourToggle = () => {
    if (tourActive) {
      endTour();
    } else {
      if (user) {
        startTour(user.role);
      }
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#d2d2d7]">
        <div className="max-w-[980px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[44px]">
            <Link to="/" className="flex items-center">
              <span className="text-lg sm:text-xl font-semibold text-[#1d1d1f]">ApprenticeshipMatch</span>
            </Link>

            {!user ? (
              <>
                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-8">
                  <Link to="/how-it-works" className="text-sm text-[#1d1d1f] hover:text-[#0071e3] transition-colors">How It Works</Link>
                  <Link to="/for-apprentices" className="text-sm text-[#1d1d1f] hover:text-[#0071e3] transition-colors">For Apprentices</Link>
                  <Link to="/for-employers" className="text-sm text-[#1d1d1f] hover:text-[#0071e3] transition-colors">For Employers</Link>
                  <Link to="/pricing" className="text-sm text-[#1d1d1f] hover:text-[#0071e3] transition-colors">Pricing</Link>
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-[#0071e3] text-white hover:bg-[#0077ed] rounded-full px-4 py-1 h-8 text-sm"
                    data-testid="nav-login-btn"
                  >
                    Login
                  </Button>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 text-[#1d1d1f]"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-4">
                {user && (
                  <Button
                    onClick={handleTourToggle}
                    variant={tourActive ? "destructive" : "secondary"}
                    className="rounded-full h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                    data-testid="demo-tour-btn"
                  >
                    <Play className="w-3 h-3 mr-1" />
                    <span className="hidden sm:inline">{tourActive ? 'End Tour' : 'Demo Tour'}</span>
                    <span className="sm:hidden">Tour</span>
                  </Button>
                )}
                <span className="text-xs sm:text-sm text-[#86868b] truncate max-w-[100px] sm:max-w-none">{user.name}</span>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && !user && (
        <div className="fixed inset-0 z-40 bg-white md:hidden" style={{ top: '44px' }}>
          <div className="px-6 py-8 space-y-6">
            <Link 
              to="/how-it-works" 
              className="block text-2xl text-[#1d1d1f] hover:text-[#0071e3] py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works
            </Link>
            <Link 
              to="/for-apprentices" 
              className="block text-2xl text-[#1d1d1f] hover:text-[#0071e3] py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Apprentices
            </Link>
            <Link 
              to="/for-employers" 
              className="block text-2xl text-[#1d1d1f] hover:text-[#0071e3] py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Employers
            </Link>
            <Link 
              to="/pricing" 
              className="block text-2xl text-[#1d1d1f] hover:text-[#0071e3] py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/login');
              }}
              className="w-full bg-[#0071e3] text-white hover:bg-[#0077ed] rounded-full px-6 py-6 h-auto text-lg mt-4"
              data-testid="nav-login-btn-mobile"
            >
              Login
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
